import React, { useState, useEffect } from "react";
import { Modal, Button, Table, Form, Input, Space, message } from "antd";
import { PlusOutlined, DeleteOutlined, ReloadOutlined } from "@ant-design/icons";
import axios from "axios";

/**
 * Composant générique et paramétrable
 *
 * Props :
 * - apiUrl (string) : URL de l’API
 * - title (string) : titre de la fenêtre
 * - trigger (ReactNode) : élément déclencheur (bouton, icône, etc.)
 * - columns (array) : colonnes à afficher dans le tableau (Ant Design format)
 * - formFields (array) : champs du formulaire [{ name, label, placeholder, type }]
 * - rowKey (string) : clé unique des éléments (ex: "id")
 */
const ModifyItemPage = ({
  apiUrl,
  ot_tag,
  title = "Gestion des éléments",
  trigger,
  columns = [],
  formFields = [],
  rowKey = "id",
}) => {
  const [visible, setVisible] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  /** 🔹 Charger les données */
  const fetchData = async () => {
    setLoading(true);
    try {
      // const updatedapiUrl = ot_tag === 'cds' ? apiUrl : `${apiUrl}?ot_tag=${ot_tag}`;
      const updatedapiUrl = `${apiUrl}?ot_tag=${ot_tag}`;
      const res = await axios.get(updatedapiUrl);
      setItems(res.data);
    } catch (error) {
      message.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  /** 🔹 Ajouter un élément */
  const handleAdd = async (values) => {
    try {
      const updatedValues = {
        ...values,
        ot_tag: ot_tag,
      };
      console.log(updatedValues)
      await axios.post(apiUrl, updatedValues);
      message.success("Élément ajouté !");
      form.resetFields();
      fetchData();
    } catch (error) {
      message.error("Erreur lors de l’ajout");
    }
  };

  /** 🔹 Supprimer un élément (directement au clic) */
  const handleDelete = async (id) => {
    try {
      await axios.delete(`${apiUrl}${id}/`);
      message.success("Élément supprimé !");
      fetchData();
    } catch (error) {
      message.error("Erreur lors de la suppression");
    }
  };

  useEffect(() => {
    if (visible) fetchData();
  }, [visible]);

  /** 🔹 Colonnes dynamiques + bouton Supprimer direct */
  const tableColumns = [
    ...columns,
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record[rowKey])}
          >
            Supprimer
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      {/* 🔸 Élément déclencheur */}
      <span onClick={() => setVisible(true)} style={{ cursor: "pointer"}}>
        {trigger}
      </span>

      {/* 🔸 Fenêtre modale */}
      <Modal
        open={visible}
        title={title}
        onCancel={() => setVisible(false)}
        footer={null}
        width={700}
      >
        {/* Bouton de rafraîchissement */}
        <div className="flex justify-end mb-2">
          <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
            Rafraîchir
          </Button>
        </div>

        {/* 🔹 Tableau des éléments */}
        <Table
          dataSource={items}
          columns={tableColumns}
          rowKey={rowKey}
          loading={loading}
          pagination={false}
        />

        {/* 🔹 Formulaire d’ajout */}
        <div style={{ marginTop: 24 }}>
          <h4>Ajouter un nouvel élément</h4>
          <Form form={form} layout="inline" onFinish={handleAdd}>
            {formFields.map((field) => (
              <Form.Item
                key={field.name}
                name={field.name}
                label={field.label}
                rules={[{ required: true, message: `Veuillez saisir ${field.label.toLowerCase()}` }]}
              >
                <Input
                  placeholder={field.placeholder || field.label}
                  type={field.type || "text"}
                />
              </Form.Item>
            ))}
            <Form.Item>
              <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>
                Ajouter
              </Button>
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </>
  );
};

export default ModifyItemPage;
